import { UserId } from '../value-objects/UserId';
import { Email } from '../value-objects/Email';
import { Tenant } from './Tenant';

export type UserRole = 'admin' | 'manager' | 'user';

/**
 * Interfaz para reconstruir User desde persistencia
 */
export interface UserPersistence {
  id: number;
  username: string;
  email: string;
  role: UserRole;
  tenant?: {
    id: number | string;
    nombre: string;
    emailAdministrador?: string;
    baseDeDatos?: string;
    connectionString?: string;
    timeZoneId?: string;
    fechaActivoUTC?: Date | string;
    active?: boolean;
    fechaVencimiento?: Date | string;
    fechaInactivoUTC?: Date | string;
    planAdminId?: number;
  } | null;
  createdAt: Date | string;
  lastLogin?: Date | string;
  mustChangePassword?: boolean;
}

export class User {
  private constructor(
    private readonly _id: UserId,
    private readonly _username: string,
    private readonly _email: Email,
    private readonly _role: UserRole,
    private readonly _tenant: Tenant | null,
    private readonly _createdAt: Date,
    private readonly _lastLogin?: Date,
    private readonly _mustChangePassword: boolean = false
  ) {}

  /**
   * Crea una nueva instancia de User (nueva entidad)
   * Incluye validaciones y lógica de negocio
   */
  static create(
    id: number,
    username: string,
    email: string,
    role: UserRole,
    tenant: { id: number | string; nombre: string; emailAdministrador?: string } | null = null,
    createdAt: Date = new Date(),
    lastLogin?: Date,
    mustChangePassword: boolean = false
  ): User {
    // Validaciones
    if (!username || username.trim().length === 0) {
      throw new Error('Username cannot be empty');
    }

    let tenantEntity: Tenant | null = null;
    
    if (tenant) {
      // Crear tenant simplificado para el contexto del usuario
      const tenantEmail = tenant.emailAdministrador || email;
      tenantEntity = Tenant.create(
        tenant.id,
        tenant.nombre,
        tenantEmail,
        'default_db',
        'default_connection',
        'America/Argentina/Buenos_Aires',
        new Date(),
        true
      );
    }
    
    return new User(
      UserId.create(id),
      username.trim(),
      Email.create(email),
      role,
      tenantEntity,
      createdAt,
      lastLogin,
      mustChangePassword
    );
  }

  /**
   * Reconstruye un User desde persistencia (sin validaciones)
   * Se usa cuando se carga desde BD o API
   */
  static reconstitute(data: UserPersistence): User {
    let tenantEntity: Tenant | null = null;
    
    if (data.tenant) {
      tenantEntity = Tenant.reconstitute({
        id: data.tenant.id,
        nombre: data.tenant.nombre,
        emailAdministrador: data.tenant.emailAdministrador || data.email,
        baseDeDatos: data.tenant.baseDeDatos || 'default_db',
        connectionString: data.tenant.connectionString || 'default_connection',
        timeZoneId: data.tenant.timeZoneId || 'America/Argentina/Buenos_Aires',
        fechaActivoUTC: typeof data.tenant.fechaActivoUTC === 'string' 
          ? new Date(data.tenant.fechaActivoUTC) 
          : data.tenant.fechaActivoUTC || new Date(),
        active: data.tenant.active !== undefined ? data.tenant.active : true,
        fechaVencimiento: data.tenant.fechaVencimiento 
          ? (typeof data.tenant.fechaVencimiento === 'string' 
            ? new Date(data.tenant.fechaVencimiento) 
            : data.tenant.fechaVencimiento)
          : undefined,
        fechaInactivoUTC: data.tenant.fechaInactivoUTC
          ? (typeof data.tenant.fechaInactivoUTC === 'string'
            ? new Date(data.tenant.fechaInactivoUTC)
            : data.tenant.fechaInactivoUTC)
          : undefined,
        planAdminId: data.tenant.planAdminId
      });
    }

    return new User(
      UserId.fromValue(data.id),
      data.username,
      Email.fromValue(data.email),
      data.role,
      tenantEntity,
      typeof data.createdAt === 'string' ? new Date(data.createdAt) : data.createdAt,
      data.lastLogin 
        ? (typeof data.lastLogin === 'string' ? new Date(data.lastLogin) : data.lastLogin)
        : undefined,
      Boolean(data.mustChangePassword)
    );
  }

  // Getters
  get id(): UserId {
    return this._id;
  }

  get username(): string {
    return this._username;
  }

  get email(): Email {
    return this._email;
  }

  get role(): UserRole {
    return this._role;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get lastLogin(): Date | undefined {
    return this._lastLogin;
  }

  get mustChangePassword(): boolean {
    return this._mustChangePassword;
  }

  getTenant(): Tenant | null {
    return this._tenant;
  }

  /**
   * Cambia el tenant del usuario
   * Retorna un nuevo User (inmutabilidad)
   */
  changeTenant(tenant: Tenant): User {
    return new User(
      this._id,
      this._username,
      this._email,
      this._role,
      tenant,
      this._createdAt,
      this._lastLogin,
      this._mustChangePassword
    );
  }

  /**
   * Actualiza el último login
   * Retorna un nuevo User (inmutabilidad)
   */
  updateLastLogin(): User {
    return new User(
      this._id,
      this._username,
      this._email,
      this._role,
      this._tenant,
      this._createdAt,
      new Date(),
      this._mustChangePassword
    );
  }

  withMustChangePassword(mustChangePassword: boolean): User {
    return new User(
      this._id,
      this._username,
      this._email,
      this._role,
      this._tenant,
      this._createdAt,
      this._lastLogin,
      mustChangePassword
    );
  }
}
